import React, { useState, useEffect } from 'react';
import { Box, Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAccessibility } from '../contexts/AccessibilityContext';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { collection, query, where, getDocs, updateDoc, doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { formatDate } from '../utils/dateUtils';
import { logAuditEvent } from '../firebase/auditService';
import { appTypographySx } from '../theme';
import { SERIF, SANS, MONO, todayTokens } from '../theme/todayTokens';
import { recordStyles } from './property/PropertyRecord';

const ArchivedTenants = () => {
  const { isDarkMode } = useTheme();
  const t = todayTokens(isDarkMode);
  const s = recordStyles(isDarkMode);
  const { currentUser } = useAuth();
  const { fontSize, lineHeight, letterSpacing, fontWeight } = useAccessibility();
  const [archivedTenants, setArchivedTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchArchivedTenants();
  }, [currentUser]);

  const fetchArchivedTenants = async () => {
    try {
      setLoading(true);
      const tenantsRef = collection(db, 'tenants');
      const q = query(tenantsRef, where('archived', '==', true));
      const querySnapshot = await getDocs(q);

      const tenants = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setArchivedTenants(tenants);
    } catch (err) {
      console.error('Error fetching archived tenants:', err);
      setError('Failed to load archived tenants');
    } finally {
      setLoading(false);
    }
  };

  const handleUnarchive = async (tenantId) => {
    try {
      // Get tenant data before unarchiving
      const tenantDoc = doc(db, 'tenants', tenantId);
      const tenantSnapshot = await getDoc(tenantDoc);

      if (!tenantSnapshot.exists()) {
        throw new Error('Tenant not found');
      }

      const tenantData = tenantSnapshot.data();

      // Update the tenant document
      await updateDoc(tenantDoc, {
        archived: false,
        archivedDate: null
      });

      // Prepare audit log data, ensuring no undefined values
      const auditData = {
        eventType: 'tenant',
        description: `Tenant unarchived: ${tenantData?.firstName || ''} ${tenantData?.lastName || ''}`,
        tenantId: tenantId,
        tenantName: `${tenantData?.firstName || ''} ${tenantData?.lastName || ''}`,
        tenantEmail: tenantData?.email || ''
      };

      // Only add property fields if they exist and are not undefined
      if (tenantData?.propertyId) {
        auditData.propertyId = tenantData.propertyId;
      }

      if (tenantData?.propertyAddress) {
        auditData.propertyAddress = tenantData.propertyAddress;
      }

      // Log the unarchive action to the main audit logs
      await logAuditEvent(auditData);

      // Refresh the list
      await fetchArchivedTenants();
    } catch (err) {
      console.error('Error unarchiving tenant:', err);
      setError('Failed to unarchive tenant');
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, ...appTypographySx(isDarkMode) }}>
        <Box component="span" sx={{ ...s.eyebrow, fontSize, lineHeight, letterSpacing, fontWeight }}>Loading…</Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, ...appTypographySx(isDarkMode) }}>
        <Alert
          severity="error"
          sx={{
            '& .MuiAlert-message': {
              fontSize: fontSize,
              lineHeight: lineHeight,
              letterSpacing: letterSpacing,
              fontWeight: fontWeight
            }
          }}
        >
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box role="main" aria-label="Archived Tenants" sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3, ...appTypographySx(isDarkMode) }}>
      {/* ── Header ── */}
      <Box component="section" sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, minWidth: 0 }}>
        <Box>
          <Box component="button" type="button" onClick={() => navigate('/tenants')} sx={{ ...s.underlineLink, minHeight: 36, fontSize: 13 }}>
            Back to tenants
          </Box>
        </Box>
        <Box sx={{ ...s.eyebrow, fontSize: 12 }}>Archive · Tenants</Box>
        <Box component="h1" sx={{ m: 0, fontFamily: SERIF, fontWeight: 400, fontSize: 'clamp(36px, 5vw, 64px)', lineHeight: 0.95, letterSpacing: '-0.02em', color: t.ink }}>
          Archived Tenants
        </Box>
      </Box>

      {archivedTenants.length === 0 ? (
        <Alert
          severity="info"
          sx={{
            '& .MuiAlert-message': {
              fontSize: fontSize,
              lineHeight: lineHeight,
              letterSpacing: letterSpacing,
              fontWeight: fontWeight
            }
          }}
        >
          No archived tenants found
        </Alert>
      ) : (
        <Box sx={{ ...s.card, overflow: 'hidden' }}>
          {archivedTenants.map((tenant) => (
            <Box
              key={tenant.id}
              role="article"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                px: { xs: 2, sm: 2.5 },
                py: 2,
                borderBottom: `1px solid ${t.lineSoft}`,
                '&:last-child': { borderBottom: 0 },
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ fontFamily: SANS, fontSize: 15, fontWeight: 600, color: t.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {tenant.firstName} {tenant.lastName}
                </Box>
                <Box sx={{ fontFamily: MONO, fontSize: 11, letterSpacing: '0.08em', color: t.muted, mt: 0.5 }}>
                  Archived on: {formatDate(tenant.archivedDate)}
                </Box>
              </Box>
              <Box
                component="button"
                type="button"
                onClick={() => handleUnarchive(tenant.id)}
                aria-label="Unarchive tenant"
                sx={{ ...s.underlineLink, minHeight: 36, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em', flexShrink: 0 }}
              >
                Unarchive
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ArchivedTenants;
